import { useEffect, useRef, useState, useCallback } from 'react';
import io from 'socket.io-client';

const API_URL = process.env.REACT_APP_API_URL || 'https://apiprov.conduto.com.ec';

export const useSocket = () => {
  const socketRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [formularioData, setFormularioData] = useState({});

  const connectSocket = useCallback(() => {
    const token = localStorage.getItem('token');

    if (!token) {
      return;
    }

    if (socketRef.current?.connected) {
      return;
    }

    if (socketRef.current) {
      socketRef.current.removeAllListeners();
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    socketRef.current = io(API_URL, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
      autoConnect: true,
      forceNew: true
    });

    socketRef.current.on('connect', () => {
      setIsConnected(true);
    });

    socketRef.current.on('disconnect', () => {
      setIsConnected(false);
    });

    socketRef.current.on('connect_error', () => {
      setIsConnected(false);
    });

    socketRef.current.on('formulario_usuarios_data', (data) => {
      setFormularioData(prev => ({
        ...prev,
        [data.formulario_id]: data.usuarios
      }));
    });

    socketRef.current.on('formulario_usuario_update', (data) => {
      if (!data.formulario_id || !data.user_id) return;

      setFormularioData(prev => {
        const currentUsers = prev[data.formulario_id] || [];

        if (data.action === 'removed') {
          return {
            ...prev,
            [data.formulario_id]: currentUsers.map(u => {
              if (u.id_usuario !== data.user_id) return u;
              return {
                ...u,
                formulario: {
                  ...(u.formulario || {}),
                  ...(data.formulario_info || {}),
                  visible: 0,
                  estado: u.formulario?.estado || 'activo'
                }
              };
            })
          };
        }

        const updateUser = (users) => users.map(u => {
          if (u.id_usuario !== data.user_id) return u;
          return {
            ...u,
            formulario: {
              ...(u.formulario || {}),
              ...(data.formulario_info || {}),
              visible: data.formulario_info?.visible ?? u.formulario?.visible ?? 1,
              estado: data.formulario_info?.estado ?? u.formulario?.estado ?? 'activo'
            }
          };
        });

        if (['added', 'reactivated', 'reassigned', 'dates_updated', 'status_changed'].includes(data.action)) {
          const exists = currentUsers.find(u => u.id_usuario === data.user_id);
          if (!exists) {
            return {
              ...prev,
              [data.formulario_id]: [
                ...currentUsers,
                {
                  id_usuario: data.user_id,
                  formulario: data.formulario_info || {}
                }
              ]
            };
          }
          return {
            ...prev,
            [data.formulario_id]: updateUser(currentUsers)
          };
        }

        return {
          ...prev,
          [data.formulario_id]: updateUser(currentUsers)
        };
      });
    });

  }, []);

  const disconnectSocket = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.removeAllListeners();
      socketRef.current.disconnect();
      socketRef.current = null;
      setIsConnected(false);
    }
  }, []);

  useEffect(() => {
    connectSocket();
    return () => {
      disconnectSocket();
    };
  }, [connectSocket, disconnectSocket]);

  const joinFormularioUpdates = useCallback((formularioId) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('join_formulario_updates', { formulario_id: formularioId });
    }
  }, []);

  const leaveFormularioUpdates = useCallback((formularioId) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('leave_formulario', { formulario_id: formularioId });
    }
  }, []);

  const requestFormularioUsuarios = useCallback((formularioId) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('get_formulario_usuarios', { formulario_id: formularioId });
    }
  }, []);

  return {
    socket: socketRef.current,
    isConnected,
    formularioData,
    joinFormularioUpdates,
    leaveFormularioUpdates,
    requestFormularioUsuarios
  };
};